"use client";
import { lazy } from "react";
import classNames from "classnames";
// import { Box, Stack } from "@fastlooptech/layout-components";
import { ImageType } from "@/types/app.type";
import { Body } from "@/client/foundations/Typography";
import { spaceUnit } from "@/helpers/utils.helper";
import { useResponsive } from "@/client/hooks/useResponsive";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { renderToString } from "react-dom/server";
import { Box, Stack } from "@/layouts/LayoutComponents";

const ImageWrapper = lazy(() => import("@/components/ImageWrapper"));

export interface QuoteCardProps {
  "data-test-id"?: string;
  /**
   * The QuoteCard className.
   * @type {string}
   */
  className?: string;
  /**
   * The description of the QuoteCard.
   * @type string
   * @required
   */
  description: string;
  /**
   * The image source of the QuoteCard.
   * @type ImageType
   * @required
   */
  image: ImageType;
  /**
   * The description of the QuoteCard.
   * @type string
   * @optional
   */
  author?: string;
  /**
   * The description of the QuoteCard.
   * @type string
   * @optional
   */
  jobPosition?: string;
  /**
   * Enable animation triggered when the element is visible.
   * @type boolean
   * @optional
   */
  animated?: boolean;
  /**
   * The animation type triggered whe the element is visible. Default is fadeIn
   * @type string
   * @optional
   */
  animationType?: string;
}

export function QuoteCard(props: QuoteCardProps) {
  const { isSm } = useResponsive();
  const { author, description, image, jobPosition, animated, animationType } = props;

  let imagePlaceholder: any = {
    width: image?.width || 300,
    height: image?.height || 100,
    // src: image ? (image + '?fm=avif&w=300&h=100') : "https://placehold.co/300x80",
    src: (typeof (image) === "string") ? image : (image?.src || "https://placehold.co/300x80"),
  };

  let cardDescription = (description && typeof description === 'object') ? renderToString(documentToReactComponents(description)) : description;

  return (
    <Box
      data-test-id={props["data-test-id"]}
      column
      className={classNames(
        "relative bg-interactive-quote-card rounded-quote-card flex justify-between",
        isSm ? "w-[544px] p-3" : "w-[256px] p-2 flex-wrap",
        props.className,
      )}
      data-aos={animated ? (animationType || 'fade-in') : ''}
    >
      <Stack column gap={spaceUnit * 2} vAlignContent="center" hAlignContent="left">
        {/* <pre>{JSON.stringify(imagePlaceholder, null, 2)}</pre> */}
        <Box className="h-[100px]">
          <ImageWrapper
            src={imagePlaceholder?.src}
            alt="quote-card-image"
            placeholder="blur"
            blurDataURL={imagePlaceholder?.src}
            width={imagePlaceholder?.width}
            height={imagePlaceholder?.height}
            priority
            // loading="lazy"
            loader={({ src }) => src}
            className="m-0 object-contain object-left"
          />
        </Box>
        <Body size="xxs">{cardDescription}</Body>
      </Stack>
      <Stack gap={spaceUnit * 0.5} column vAlignContent="center" hAlignContent="right">
        {author ? (
          <Body size="l" className="text-body-l-bold">
            {author}
          </Body>
        ) : null}
        {jobPosition ? (
          <Body size="s" className="text-right">
            {jobPosition}
          </Body>
        ) : null}
      </Stack>
    </Box>
  );
}
